import React, { useMemo, type PropsWithChildren } from 'react';
import { styled } from '@compiled/react';
import { Box, Flex, xcss } from '@atlaskit/primitives';
import { token } from '@atlaskit/tokens';
import Tooltip from '@atlaskit/tooltip';
import {
	ORIGINAL_ESTIMATE,
	STORY_POINTS,
} from '@atlassian/jira-backlog-epics-store/src/constants.tsx';
import type { EstimationInfo } from '@atlassian/jira-backlog-epics-store/src/types.tsx';
import { expVal } from '@atlassian/jira-feature-experiments';
import { componentWithCondition } from '@atlassian/jira-feature-flagging-utils';
import { useIntl } from '@atlassian/jira-intl';
import { timeTrackingFormatter } from '@atlassian/jira-time-tracking-formatter/src/main.tsx';
import type { TimeTrackingOptions } from '@atlassian/jira-time-tracking-formatter/src/types.tsx';
import { isVisualRefreshEnabled } from '@atlassian/jira-visual-refresh-rollout/src/feature-switch/index.tsx';
import { roundToTwoDecimalPlaces } from '../../utils.tsx';
import { cardColor } from '../card/constants.tsx';
import messages from './messages.tsx';

type EstimateFieldProps = {
	estimationInfo: EstimationInfo;
	timeTrackingOptions: TimeTrackingOptions;
};

// TODO: move to ui/card-contents level since this is not used in the version panel
const EstimateField = ({ estimationInfo, timeTrackingOptions }: EstimateFieldProps) => {
	const intl = useIntl();
	const { formatMessage } = intl;

	const formattedEstimationInfo = useMemo(() => {
		const {
			estimationType,
			doneEstimate: unformattedDoneEstimate,
			totalEstimate: unformattedTotalEstimate,
		} = estimationInfo;
		const isTimeEstimate = estimationType === ORIGINAL_ESTIMATE;
		const doneEstimate = isTimeEstimate
			? timeTrackingFormatter(unformattedDoneEstimate, timeTrackingOptions, intl)
			: roundToTwoDecimalPlaces(unformattedDoneEstimate);
		const totalEstimate = isTimeEstimate
			? timeTrackingFormatter(unformattedTotalEstimate, timeTrackingOptions, intl)
			: roundToTwoDecimalPlaces(unformattedTotalEstimate);

		return {
			...estimationInfo,
			doneEstimate,
			totalEstimate,
		};
	}, [estimationInfo, intl, timeTrackingOptions]);

	const { estimationType, name, doneEstimate, totalEstimate, percentageCompleted, notEstimated } =
		formattedEstimationInfo;

	const [totalEstimateMessage, tooltipMessage] = useMemo(() => {
		if (estimationType === STORY_POINTS) {
			return [
				formatMessage(messages.points, { totalEstimate }),
				formatMessage(messages.pointsTooltip, { doneEstimate, totalEstimate }),
			];
		}
		return [
			totalEstimate,
			estimationType === ORIGINAL_ESTIMATE
				? formatMessage(messages.timeEstimateTooltip, { doneEstimate, totalEstimate })
				: formatMessage(messages.customEstimateTooltip, {
						doneEstimate,
						totalEstimate,
						name,
					}),
		];
	}, [estimationType, name, formatMessage, doneEstimate, totalEstimate]);
	return (
		<EstimateContainer>
			<Flex as="dl" justifyContent="space-between" xcss={sectionWrapperStyles}>
				<EstimateDT>{formatMessage(messages.label)}</EstimateDT>
				<EstimateDD>{totalEstimateMessage}</EstimateDD>
			</Flex>
			<Tooltip content={tooltipMessage}>
				<Subtext>
					{formatMessage(messages.percentageCompleted, {
						percentageCompleted,
					})}
				</Subtext>
			</Tooltip>
			<Subtext>
				{formatMessage(
					expVal('issue-terminology-refresh-m2-replace', 'isEnabled', false)
						? messages.unestimatedIssuesIssueTermRefresh
						: messages.unestimatedIssues,
					{ notEstimated },
				)}
			</Subtext>
		</EstimateContainer>
	);
};

const sectionWrapperStyles = xcss({
	margin: '0',
	padding: '0',
});

/**
 * These are used in the version panel as well so they are exported from here.
 * Ideally the version panel should define its own colocated compiled components to use instead of importing from the epic panel.
 */

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled, @atlaskit/ui-styling-standard/no-exported-styles -- Ignored via go/DSP-18766
export const TotalEstimate = styled.div({
	display: 'flex',
	justifyContent: 'space-between',
	alignItems: 'center',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled, @atlaskit/ui-styling-standard/no-exported-styles -- Ignored via go/DSP-18766
export const EstimateContainer = styled.div({
	display: 'flex',
	flexDirection: 'column',
	gap: token('space.050'),
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled, @atlaskit/ui-styling-standard/no-exported-styles -- Ignored via go/DSP-18766
export const SubtextOld = styled.span({
	font: token('font.body.small'),
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	color: cardColor.default.textColor,
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled, @atlaskit/ui-styling-standard/no-exported-styles -- Ignored via go/DSP-18766
export const EstimateDTOld = styled.dt({
	fontWeight: token('font.weight.regular'),
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled, @atlaskit/ui-styling-standard/no-exported-styles -- Ignored via go/DSP-18766
export const EstimateDDOld = styled.dd({
	margin: 0,
});

export const EstimateDT = componentWithCondition(
	isVisualRefreshEnabled,
	({ children }: PropsWithChildren<{}>) => (
		<Box as="dt" xcss={estimateLabelStyles}>
			{children}
		</Box>
	),
	({ children }: PropsWithChildren<{}>) => <EstimateDTOld>{children}</EstimateDTOld>,
);

export const EstimateDD = componentWithCondition(
	isVisualRefreshEnabled,
	({ children }: PropsWithChildren<{}>) => (
		<Box as="dd" xcss={estimateMessageStyles}>
			{children}
		</Box>
	),
	({ children }: PropsWithChildren<{}>) => <EstimateDDOld>{children}</EstimateDDOld>,
);

export const Subtext = componentWithCondition(
	isVisualRefreshEnabled,
	({ children }: PropsWithChildren<{}>) => <Box xcss={subtextStyles}>{children}</Box>,
	({ children }: PropsWithChildren<{}>) => <SubtextOld>{children}</SubtextOld>,
);

const estimateLabelStyles = xcss({
	font: 'font.body',
	color: 'color.text.subtle',
});

const estimateMessageStyles = xcss({
	font: 'font.body',
	color: 'color.text',
	marginTop: 'space.0',
});

const subtextStyles = xcss({
	font: 'font.body.UNSAFE_small',
	color: 'color.text.subtle',
});

export default EstimateField;
